<script setup>
import { ref, onMounted } from 'vue'

import { DxList } from 'devextreme-vue/list'
import { getI18n } from '@/plugins/i18n'

import { useWebSiteStore } from '@/stores/web-site.store'
import { usePlanningStore } from '@/stores/planning.store'

import { useODataSource, useCurrencyColumnFormatter } from '@/composables/data-grid'
import { useDetectDevice } from '@/composables/device'

import FilterSortSelector from '@/components/shared/FilterSortSelector.vue'
import ProjectListItem from '@/components/shared/ProjectListItem.vue'
import ProjectEdit from '@/components/shared/ProjectEdit.vue'

import CostReportRowsGrid from '@/components/accounting/CostReportRowsGrid.vue'
import CostReportDetail from '@/components/accounting/CostReportDetail.vue'
import ReportEdit from '@/components/accounting/ReportEdit.vue'
import ReportFileDialog from '@/components/accounting/ReportFileDialog.vue'

const { isMobile } = useDetectDevice()

const i18n = getI18n()
const webSiteStore = useWebSiteStore()
const planningStore = usePlanningStore()
const editItem = ref(null)
const detailItem = ref(null)
const reportEditItem = ref(null)
const generatedReport = ref(null)
const isEmptyDataSource = ref(false)

webSiteStore.fabActions = [
  {
    id: 'add',
    icon: 'mdi-file-chart-outline',
    onClick: () => { reportEditItem.value = {} },
  },
]

const onReportGenerated = (report) => {
  reportEditItem.value = null
  generatedReport.value = report
}

const dataSource = useODataSource(planningStore.api.getAbsoluteUrl() + planningStore.api.projectsUrl, {
  onChanged: () => {
    isEmptyDataSource.value = dataSource.totalCount() === 0
  },
  searchExpr: 'search_field',
})

const onProjectClick = (item) => { detailItem.value = item }

const onChange = async () => {
  editItem.value = null
  detailItem.value = null
  dataSource.reload()
}

const formatCurrency = (project) => {
  if (project.total_cost == null) return null
  return useCurrencyColumnFormatter(project, 'total_cost', project.currency_ref)
}

onMounted(async () => {
  if (webSiteStore.editedProjectId !== null) {
    if (webSiteStore.editedProjectId === 0) {
      editItem.value = {}
    } else {
      detailItem.value = (await planningStore.fetchProjectByIdAsync(webSiteStore.editedProjectId)).data
    }
    webSiteStore.editedProjectId = null
  }
})
</script>
<template>
  <div>
    <q-dialog :model-value="reportEditItem !== null" maximized persistent>
      <report-edit
        v-if="reportEditItem !== null"
        :model-value="reportEditItem"
        @update="onReportGenerated"
        @cancel="reportEditItem = null"
      />
    </q-dialog>
    <q-dialog :model-value="generatedReport !== null" maximized @hide="generatedReport = null">
      <report-file-dialog v-if="generatedReport !== null" :report="generatedReport" @cancel="generatedReport = null" />
    </q-dialog>
    <filter-sort-selector :data-source="dataSource" />
    <q-card flat>
      <q-card-section class="q-pa-none">
        <dx-list
          v-show="!isEmptyDataSource"
          :data-source="dataSource"
          height="calc(100vh - 8rem)"
          page-load-mode="scrollBottom"
          class="events-list"
          item-template="item"
          selection-mode="none"
          :active-state-enabled="false"
        >
          <template #item="{ data: project }">
            <q-item>
              <project-list-item
                :key="project.id"
                v-ripple
                class="q-pa-sm"
                clickable
                hide-time
                dense
                status-left
                :item="project"
                :disabled="project.archived"
                @click="onProjectClick"
              >
                <template #above-date>
                  <q-item-label class="text-no-wrap text-right text-bold">
                    {{ formatCurrency(project) || '-' }} &mdash; <q-icon name="mdi-timer-outline" class="q-mr-xs" />{{ project.total_reported_minutes != null ? (project.total_reported_minutes / 60).toFixed(2) : '-' }} {{ i18n.t('common.hoursAlt') }}
                  </q-item-label>
                  <q-item-label class="text-no-wrap text-right text-grey text-caption">
                    <q-icon name="mdi-briefcase-outline" class="q-mr-xs" />{{ project.total_worked_minutes != null ? (project.total_worked_minutes / 60).toFixed(2) : '-' }} {{ i18n.t('common.hoursAlt') }}
                  </q-item-label>
                </template>
              </project-list-item>
            </q-item>
          </template>
        </dx-list>
        <q-dialog :model-value="editItem !== null" class="dialog-xs" :maximized="isMobile">
          <project-edit
            v-if="editItem !== null"
            :model-value="editItem"
            @update="onChange"
            @cancel="onChange"
            @delete="onChange"
          />
        </q-dialog>
        <q-dialog :model-value="detailItem !== null" maximized>
          <cost-report-detail
            v-if="detailItem !== null && isMobile"
            :project="detailItem"
            @update="dataSource.reload()"
            @cancel="detailItem = null"
          />
          <q-card
            v-else-if="detailItem !== null"
            flat
          >
            <q-toolbar>
              <q-btn
                flat
                round
                dense
                icon="mdi-arrow-left"
                @click="detailItem = null"
              />
              <q-toolbar-title>{{ detailItem.name }}</q-toolbar-title>
            </q-toolbar>
            <q-card-section class="q-pa-none">
              <cost-report-rows-grid :project="detailItem" />
            </q-card-section>
          </q-card>
        </q-dialog>
      </q-card-section>
    </q-card>
  </div>
</template>
